import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useCelloService } from '../services/cello/cello-api-service';
import i18n from '../services/i18n/i18n.service';
import { useReferralService } from '../services/referral.service';

interface CelloContextType {
  isCelloBooted: boolean;
  setIsCelloBooted: (isBooted: boolean) => void;
}

const CelloContext = createContext<CelloContextType | undefined>(undefined);

export const CelloProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isCelloBooted, setIsCelloBooted] = useState(false);
 const { currentUser, currentCtUser } = useAuth();
  const celloApiService = useCelloService();
  const referralService = useReferralService();
  
  // Handle user identification when user data becomes available
  useEffect(() => {
    // Use isCelloBooted from context to prevent re-initialization
    if (!currentUser || !currentCtUser || isCelloBooted) {
      return;
    }
    
    const identifyUser = async () => {
      try {
        // Get just the essential user traits
        const traits = {
          email: currentUser.email,
          created_at: currentUser.metadata?.creationTime,
          user_id: currentCtUser.id,
          ct_id: currentCtUser.ctId,
          country: currentCtUser.country,
          locale: currentCtUser.locale,
          currency: currentCtUser.currency
        };
        
        // Get token once - no need to cache locally since we only boot once
        const response = await celloApiService.getAuthToken(traits);
        const token = response.token;
        
        // Prepare user details
        const nameParts = currentUser.displayName ? currentUser.displayName.split(' ') : [];
        const firstName = nameParts[0] || '';
        const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
        const currentLanguage = i18n.language?.split('-')[0] || 'de';
        
        // Boot Cello with the token and user data - runs exactly once
        if (window.cello) {
          window.cello.cmd.push(function(cello: any) {
            cello.boot({
              productId: import.meta.env.VITE_CELLO_APP_ID,
              token: token,
              language: currentLanguage,
              productUserDetails: {
                firstName: firstName,
                lastName: lastName,
                fullName: currentUser.displayName || `${firstName} ${lastName}`.trim(),
                email: currentUser.email || ''
              }
            });
            // console.log('[Cello] User authenticated:', currentUser.uid);
            
            // Mark as booted in context
            setIsCelloBooted(true);
          });
        }
      } catch (error) {
        console.error('[Cello] Error identifying user:', error);
      }
    };
    
    identifyUser();
  }, [currentUser, currentCtUser, celloApiService, referralService]);

  useEffect(() => {
    if (!isCelloBooted) return;
    if (!window.cello) return;
    if (currentUser && currentCtUser) {
      // window.cello.cmd.push(function(cello: any) {
      //   cello.show();
      // });
    } else {
      // window.cello.cmd.push(function(cello: any) {
      //   cello.hide();
      // });
      window.cello.cmd.push(function(cello: any) {
        cello.shutdown();
      });
      setIsCelloBooted(false);
    }

    
  }, [currentUser, currentCtUser]);

  return (
    <CelloContext.Provider value={{ isCelloBooted, setIsCelloBooted }}>
      {children}
    </CelloContext.Provider>
  );
};

export const useCello = (): CelloContextType => {
  const context = useContext(CelloContext);
  if (context === undefined) {
    throw new Error('useCello must be used within a CelloProvider');
  }
  return context;
};
